import { productNameForLocale } from "../../branding/productBrand";

const productName = productNameForLocale("en");

export const enLocale = {
  login: {
    eyebrow: productName,
    titles: {
      signIn: "Sign in to continue",
      register: "Create your account",
    },
    description: "Mobile-first inbox and thread workflows through the gateway edge.",
    modeSelectorAria: "Authentication mode",
    actions: {
      signIn: "Sign In",
      register: "Register",
      createAccount: "Create Account",
      confirmRegistration: "Confirm Code",
      showPassword: "Show",
      hidePassword: "Hide",
      pleaseWait: "Please wait...",
    },
    identifierLabel: "Email",
    identifierPlaceholder: "you@example.com",
    registerIdentifierLabel: "Email",
    registerIdentifierPlaceholder: "you@example.com",
    displayNameLabel: "Visible name",
    displayNamePlaceholder: "Ada Lovelace",
    displayNameHint: "Shown in chats and contacts. Use 1-64 characters.",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    keepSignedIn: {
      label: "Keep me signed in",
      helper: "Stay signed in after browser restarts on this device. Uncheck for this browser session only.",
    },
    confirmPasswordLabel: "Confirm password",
    confirmPasswordPlaceholder: "Confirm your password",
    registrationDisclaimer: {
      title: "Registration terms and liability",
      body: [
        "By registering for the service, I confirm that I understand and accept the service terms to the extent permitted by applicable law of the Russian Federation.",
        "I agree to use the service lawfully and remain responsible for messages, files, names, group/channel descriptions, and other materials that I create, send, or distribute through the service.",
        "The service is provided for messaging \"as is\" to the extent permitted by law. The operator does not guarantee uninterrupted availability, error-free operation, compatibility with every device, or absence of failures in connectivity, infrastructure, browser, PWA runtime, network, storage, or third-party services.",
        "The operator is not liable for user actions, user materials, consequences of unlawful service use, or losses caused by circumstances outside the operator's reasonable control, except where such liability cannot be limited under mandatory law.",
        "My email address or phone number, password as a protected hash, session data, and technical account data are processed for registration, sign-in, session protection, messaging operation, and the operator's duties under applicable personal data law.",
        "If any part of this notice conflicts with mandatory user rights or legal requirements, those mandatory rules prevail.",
      ],
      versionLabel: "Terms version",
      acceptLabel: "I have read and accept these registration terms",
    },
    registrationConfirmation: {
      title: "Confirm your email",
      body: "Enter the confirmation code sent to {{email}}.",
      emailLabel: "Email",
      expiresLabel: "Expires",
      codeLabel: "Confirmation code",
      codePlaceholder: "12345678",
      resend: "Resend code",
      resending: "Resending...",
      resendSent: "New code sent.",
      resendAvailable: "Resend available after {{time}}",
    },
    errors: {
      invalidRequest: "Request validation failed. Check your credentials and try again.",
      unauthorized: "Invalid credentials. Please try again.",
      identifierTaken: "That account already exists. Sign in instead.",
      disclaimerRequired: "Accept the registration terms to create an account.",
      phoneRegistrationUnavailable: "Phone registration is temporarily unavailable. Register with email instead.",
      registrationNotFound: "No pending registration was found for that email. Start registration again.",
      invalidConfirmationCode: "That confirmation code is invalid. Check the code and try again.",
      confirmationExpired: "That confirmation code expired. Start registration again or request a new code.",
      confirmationRateLimited: "Too many confirmation attempts. Wait a moment and try again.",
      registrationDeliveryUnavailable: "The confirmation email could not be sent right now. Try again later.",
      confirmationCodeRequired: "Enter the confirmation code from your email.",
      displayNameRequired: "Visible name is required.",
      displayNameInvalid: "Visible name must be 64 characters or fewer.",
      requestFailed: "Request failed. Please retry.",
      unexpected: "Unexpected error. Please retry.",
      identifierPasswordRequired: "Email and password are required.",
      passwordMismatch: "Passwords do not match.",
    },
  },
  notifications: {
    install: {
      title: "Enable iPhone Push Popups",
      body: `Push notifications on iPhone work after ${productName} is launched from Home Screen web-app mode.`,
      steps: [
        "In Safari, tap Share and choose Add to Home Screen.",
        "Open from your Home Screen and keep Open as Web App enabled.",
        "Allow notifications when prompted inside the installed app.",
      ],
    },
    permission: {
      title: "Turn On Push Popups",
      body: "Allow notifications so new messages can appear when the app is closed or in the background.",
      action: "Enable Notifications",
      enabling: "Enabling...",
    },
  },
  contacts: {
    entry: {
      open: "Open contacts",
      close: "Close contacts",
    },
    panel: {
      eyebrow: "Contacts",
      title: "Saved people",
      close: "Close contacts",
      reload: "Reload contacts",
    },
    lookup: {
      label: "Email, phone, or user id",
      placeholder: "alice@example.com or usr_alice",
    },
    actions: {
      add: "Add contact",
      adding: "Adding contact",
      remove: "Remove {{name}}",
      removing: "Removing {{name}}",
      startChat: "Start chat with {{name}}",
      startingChat: "Starting chat with {{name}}",
    },
    states: {
      loading: "Loading contacts...",
      empty: "No saved contacts yet.",
      added: "Contact added.",
      removed: "Contact removed.",
    },
    accessibility: {
      avatarFallback: "Avatar for {{name}}",
    },
    errors: {
      duplicate: "This person is already in your contacts.",
      notFound: "No user was found for that identifier.",
      contactNotFound: "That contact is no longer saved.",
      invalid: "Enter a valid email, phone, or user id.",
      self: "You cannot add yourself as a contact.",
      unauthorized: "Session expired. Sign in again.",
      forbidden: "You do not have permission to manage contacts.",
      load: "Unable to load contacts.",
      add: "Unable to add contact.",
      remove: "Unable to remove contact.",
      startChat: "Unable to start chat.",
      generic: "Unable to manage contacts right now.",
    },
  },
  chatList: {
    shell: {
      workspaceEyebrow: "Message Workspace",
      title: "Conversations",
      openProfilePanel: "Open profile photo panel",
      signOut: "Sign out",
      newChat: "New chat",
      openTypeMenu: "Open chat type menu",
      closeTypeMenu: "Close chat type menu",
      recentConversations: "Recent conversations",
      refreshConversations: "Refresh conversations",
      loading: "Loading conversations...",
      empty: "No conversations yet. Start a chat from the + menu to continue.",
      profileFallback: "ME",
    },
    chatTypes: {
      direct: {
        label: "Direct",
        note: "One-to-one",
      },
      group: {
        label: "Group",
        note: "Shared discussion",
      },
      channel: {
        label: "Channel",
        note: "Broadcast updates",
      },
      secret: {
        label: "Secret",
        note: "E2EE in 14-03",
      },
    },
    preview: {
      direct: "Direct conversation",
      group: "Group discussion",
      channel: "Channel updates",
      secret: "Secret chat",
      fallback: "Conversation",
    },
    avatarPreview: {
      open: "Open avatar preview for {{label}}",
      close: "Close avatar preview",
      dialog: "Avatar preview for {{label}}",
      image: "Enlarged avatar for {{label}}",
    },
    unread: {
      rowLabel: "{{title}}, {{count}} unread messages",
    },
    shared: {
      add: "Add",
      remove: "Remove",
      removeWithIdentifier: "Remove {{identifier}}",
    },
    direct: {
      title: "Direct chat",
      closePanel: "Close direct panel",
      peerLabel: "Peer email or user id",
      peerPlaceholder: "you@example.com or usr_peer",
      creatingAria: "Creating chat",
      startAria: "Start direct chat",
    },
    group: {
      title: "Create group",
      closePanel: "Close group panel",
      nameLabel: "Group name",
      namePlaceholder: "release",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "Release coordination and rollout notes",
      avatarLabel: "Avatar (optional)",
      avatarSelected: "Avatar: {{fileName}}",
      addMemberLabel: "Add member by email, phone, or user id",
      memberPlaceholder: "alice@example.com or usr_alice",
      creating: "Creating group...",
      create: "Create group",
    },
    channel: {
      title: "Create channel",
      closePanel: "Close channel panel",
      nameLabel: "Channel name",
      namePlaceholder: "announcements",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "Release updates for the whole workspace",
      avatarLabel: "Avatar (optional)",
      avatarSelected: "Avatar: {{fileName}}",
      addPublisherLabel: "Add publisher by email, phone, or user id",
      publisherPlaceholder: "publisher@example.com or usr_pub",
      creating: "Creating channel...",
      create: "Create channel",
    },
    secret: {
      title: "Secret chat",
      closePanel: "Close secret panel",
      peerLabel: "Peer email, phone, or user id",
      peerPlaceholder: "alice@example.com",
      creatingAria: "Creating secret chat",
      startAria: "Start secret chat",
      badge: "Secret",
    },
    validation: {
      nameRequired: "Name is required.",
      nameOneWord: "Name must be one word without spaces.",
      nameMax: "Name must be 64 characters or fewer.",
      descriptionSingleLine: "Description must be a single line.",
      descriptionMax: "Description must be 280 characters or fewer.",
      groupReady: "Ready to create group.",
      groupRequirements: "One-word name and at least one member are required.",
      channelReady: "Ready to create channel.",
      channelRequirements: "One-word name and at least one publisher are required.",
    },
    errors: {
      loadConversations: "Failed to load conversations",
      loadProfile: "Failed to load profile",
      directPeerIdentifierRequired: "Peer email or user id is required.",
      peerIdentifierRequired: "Peer email, phone, or user id is required.",
      groupMemberIdentifierRequired: "Member identifier is required.",
      channelPublisherIdentifierRequired: "Publisher identifier is required.",
      groupAtLeastOneMember: "Add at least one member.",
      channelAtLeastOnePublisher: "Add at least one publisher.",
      secretUserNotFound: "User not found for that email/phone.",
      secretInvalidIdentifier: "Enter a valid email, phone, or user id.",
      secretForbidden: "You do not have permission to create a secret chat.",
      secretCreateFallback: "Unable to create secret chat.",
      directCreateFallback: "Unable to create direct chat",
      groupMembersNotFound: "One or more member users could not be resolved.",
      channelPublishersNotFound: "One or more publisher users could not be resolved.",
      groupInvalidRequest: "Enter a one-word group name, valid description, and at least one member.",
      channelInvalidRequest: "Enter a one-word channel name, valid description, and at least one publisher.",
      groupConflict: "A group with this setup already exists.",
      channelConflict: "A channel with this setup already exists.",
      groupForbidden: "You do not have permission to create this group.",
      channelForbidden: "You do not have permission to create this channel.",
      groupCreateFallback: "Unable to create group",
      channelCreateFallback: "Unable to create channel",
      avatarNotFound: "Selected avatar file is no longer available.",
      avatarProcessing: "Avatar upload is still processing. Try again in a moment.",
      avatarInvalid: "Select a valid image file for avatar upload.",
      avatarUploadFallback: "Unable to upload avatar right now.",
    },
  },
  chatThread: {
    shell: {
      eyebrow: "Conversation",
      secretConversation: "Secret conversation",
    },
    notifications: {
      unavailableTitle: "Conversation unavailable",
      unavailableBody:
        "This notification points to a chat that is unavailable or restricted. Return to your chats to continue.",
      dismiss: "Dismiss",
      backToChats: "Back to chats",
    },
    composer: {
      secretPlaceholder: "Write encrypted payload",
      messagePlaceholder: "Write a message",
      noAccessPlaceholder: "Chat access is required to send messages",
    },
    timeline: {
      loading: "Loading timeline...",
      empty: "No messages yet. Start the conversation.",
      deleted: "Deleted",
      messageDeleted: "Message deleted",
      loadVoice: "Load voice",
      loadPhoto: "Load photo",
      openFullSizePhoto: "Open full-size photo",
      photoAttachmentAlt: "Photo attachment",
      photoAttachmentFullAlt: "Photo attachment full size",
      photoPreview: "Photo preview",
      closePhotoPreview: "Close photo preview",
      photoLoading: "Loading full-size photo...",
      photoLoadFailed: "Full-size photo is unavailable",
      photoFailed: "Failed to send photo",
      photoSending: "Sending photo",
      voiceFailed: "Failed to send voice",
      voiceSending: "Sending voice",
      voiceMaxReached: "Max reached",
      voiceDuration: "Duration",
    },
    receipts: {
      sent: "Sent",
      read: "Read",
    },
    references: {
      reply: "Reply",
      forwarded: "Forwarded",
      attachment: "Attachment",
      unavailable: "Original message unavailable",
      openReply: "Open replied message {{sequence}}",
    },
    forward: {
      title: "Forward to",
      loading: "Loading conversations...",
      empty: "No eligible conversations",
      errors: {
        targets: "Unable to load conversations.",
        unavailable: "Unable to forward to that conversation.",
        invalid: "Forward request is invalid.",
        unauthorized: "Sign in again to forward this message.",
        failed: "Unable to forward this message.",
      },
    },
    selection: {
      mode: "Message selected",
      actionMenu: "Selected message actions",
      dismiss: "Dismiss selected message actions",
      escapeDismiss: "Press Escape to dismiss selected message actions",
    },
    voice: {
      title: "Voice message",
      cancel: "Cancel",
      reachedMax: "Reached max duration ({{duration}})",
      recorded: "Recorded {{duration}}",
      starting: "Starting...",
      recordingHoldHint: "Recording… release to preview",
      holdToRecord: "Hold to record",
      max: "Max {{duration}}",
    },
    actions: {
      saveEdit: "Save edit",
      cancelEdit: "Cancel edit",
      replyMessage: "Reply to message",
      forwardMessage: "Forward message",
      cancelReply: "Cancel reply",
      cancelForward: "Cancel forward",
      editMessage: "Edit message",
      deleteMessage: "Delete message",
      revealMessageActions: "Hold or press Enter to show message actions",
      retryPhoto: "Retry photo",
      deletePendingPhoto: "Delete pending photo",
      retryVoice: "Retry voice",
      deletePendingVoice: "Delete pending voice",
      hideMetadataEditor: "Hide metadata editor",
      editMetadata: "Edit metadata",
      hideManageMembers: "Hide manage members",
      manageMembers: "Manage members",
      hideSearch: "Hide search",
      showSearch: "Show search",
      back: "Back",
      send: "Send",
      delete: "Delete",
      mediaActions: "Media actions",
      photo: "Photo",
      voice: "Voice",
      file: "File",
      removeFile: "Remove file",
      loadFile: "Load file",
      downloadFile: "Download {{filename}}",
      sendMessage: "Send message",
    },
    files: {
      attachment: "File",
      ready: "Ready",
      uploading: "Uploading",
      uploadingProgress: "Uploading {{progress}}%",
      loading: "Loading file",
      failed: "Failed",
      errors: {
        invalid: "File type is not allowed.",
        rejected: "File was rejected.",
        unauthorized: "Sign in again to send this file.",
        failed: "Failed to send file.",
        downloadFailed: "Failed to download file.",
      },
    },
    metadataEditor: {
      title: "Edit metadata",
      helper: "Set a one-word name and optional single-line description.",
      nameLabelGroup: "Group name",
      nameLabelChannel: "Channel name",
      namePlaceholderGroup: "group_name",
      namePlaceholderChannel: "channel_name",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Optional single-line description",
      avatarLabel: "Avatar (optional)",
      avatarQueued: "Avatar queued: {{fileName}}",
      avatarWillBeRemoved: "Avatar will be removed when saved.",
      avatarRequiredForReplace: "Choose an avatar image to upload.",
      avatarActions: {
        remove: "Remove avatar",
        keep: "Keep current",
      },
      validation: {
        ready: "Ready to save metadata.",
        nameRequired: "Name is required.",
        nameOneWord: "Name must be one word without spaces.",
        nameMax: "Name must be {{max}} characters or fewer.",
        descriptionSingleLine: "Description must be a single line.",
        descriptionMax: "Description must be {{max}} characters or fewer.",
      },
      actions: {
        close: "Close",
        saving: "Saving...",
        save: "Save metadata",
      },
      success: {
        groupUpdated: "Group metadata updated.",
        channelUpdated: "Channel metadata updated.",
      },
      errors: {
        invalidRequest: "Enter a valid one-word name and description.",
        forbiddenGroup: "You do not have permission to update this group.",
        forbiddenChannel: "You do not have permission to update this channel.",
        notFoundGroup: "This group no longer exists.",
        notFoundChannel: "This channel no longer exists.",
        conflictGroup: "Group metadata update conflicted with a concurrent change.",
        conflictChannel: "Channel metadata update conflicted with a concurrent change.",
        updateFailedGroup: "Failed to update group metadata. Try again.",
        updateFailedChannel: "Failed to update channel metadata. Try again.",
        avatarNotFound: "Selected avatar file is no longer available.",
        avatarProcessing: "Avatar upload is still processing. Try again in a moment.",
        avatarInvalid: "Select a valid image file for avatar upload.",
        avatarUploadFailed: "Unable to upload avatar right now.",
      },
    },
    manageMembers: {
      title: "Manage members",
      helper: {
        canAdd: "Add by email/phone/user ID and remove existing participants.",
        groupLeaveOnly: "You can leave this group, but only admins can add members.",
        channelLeaveOnly: "You can leave this channel, but only admins/publishers can add subscribers.",
      },
      empty: "No members available for moderation.",
      identifierLabel: "Identifier",
      identifierPlaceholder: "email@example.com, +15550000000, or usr_...",
      state: {
        protected: "Protected",
      },
      actions: {
        close: "Close",
        adding: "Adding...",
        addMember: "Add member",
        addSubscriber: "Add subscriber",
        leaving: "Leaving...",
        removing: "Removing...",
        leave: "Leave",
        remove: "Remove",
      },
      success: {
        memberAdded: "Member added.",
        subscriberAdded: "Subscriber added.",
        memberRemoved: "Member removed.",
        subscriberRemoved: "Subscriber removed.",
      },
      errors: {
        identifierRequired: "Enter email, phone, or user ID.",
        invalidIdentifier: "Enter a valid email, phone, or user ID.",
        userNotFound: "No user found for that identifier.",
        addForbiddenGroup: "You do not have permission to add members to this group.",
        addForbiddenChannel: "You do not have permission to add subscribers to this channel.",
        alreadyMember: "This user is already a member.",
        alreadySubscriber: "This user is already a subscriber.",
        addFailedGroup: "Failed to add member. Try again.",
        addFailedChannel: "Failed to add subscriber. Try again.",
        removeInvalidSelection: "Select a valid member to remove.",
        removeNotFoundGroup: "This member is no longer part of the group.",
        removeNotFoundChannel: "This subscriber is no longer part of the channel.",
        removeForbiddenGroup: "You do not have permission to remove members from this group.",
        removeForbiddenChannel: "You do not have permission to remove subscribers from this channel.",
        removeConflictGroup: "This member cannot be removed due to role constraints.",
        removeConflictChannel: "This subscriber cannot be removed due to role constraints.",
        removeFailedGroup: "Failed to remove member. Try again.",
        removeFailedChannel: "Failed to remove subscriber. Try again.",
      },
    },
    lifecycle: {
      title: "Lifecycle controls",
      helper: "Close keeps history readable. Remove hides the conversation from active chat lists.",
      closedState: "This conversation is closed. History remains readable, but posting and management are disabled.",
      confirmationLabel: "Type {{phrase}} to confirm",
      confirmationPlaceholder: "Confirmation text",
      confirmationRequirement: "Destructive actions are enabled only after the confirmation text matches.",
      actions: {
        cancel: "Cancel",
        closeGroup: "Close group",
        closeChannel: "Close channel",
        removeGroup: "Remove group",
        removeChannel: "Remove channel",
        confirmClose: "Confirm close",
        confirmRemove: "Confirm remove",
        closing: "Closing...",
        removing: "Removing...",
      },
      success: {
        groupClosed: "Group closed.",
        channelClosed: "Channel closed.",
        groupRemoved: "Group removed.",
        channelRemoved: "Channel removed.",
      },
      errors: {
        closeInvalidGroup: "Group close request is invalid.",
        closeInvalidChannel: "Channel close request is invalid.",
        closeForbiddenGroup: "You do not have permission to close this group.",
        closeForbiddenChannel: "You do not have permission to close this channel.",
        closeNotFoundGroup: "This group no longer exists.",
        closeNotFoundChannel: "This channel no longer exists.",
        closeConflictGroup: "Group close conflicted with a concurrent change.",
        closeConflictChannel: "Channel close conflicted with a concurrent change.",
        closeFailedGroup: "Failed to close group. Try again.",
        closeFailedChannel: "Failed to close channel. Try again.",
        removeInvalidGroup: "Group remove request is invalid.",
        removeInvalidChannel: "Channel remove request is invalid.",
        removeForbiddenGroup: "You do not have permission to remove this group.",
        removeForbiddenChannel: "You do not have permission to remove this channel.",
        removeNotFoundGroup: "This group no longer exists.",
        removeNotFoundChannel: "This channel no longer exists.",
        removeConflictGroup: "Group removal conflicted with a concurrent change.",
        removeConflictChannel: "Channel removal conflicted with a concurrent change.",
        removeFailedGroup: "Failed to remove group. Try again.",
        removeFailedChannel: "Failed to remove channel. Try again.",
      },
    },
    directDelete: {
      title: "Delete this direct chat",
      helper: "This hides the direct chat only from your own chat list. The other person keeps their view and history.",
      confirmationLabel: "Type {{phrase}} to confirm",
      confirmationPlaceholder: "Confirmation text",
      confirmationRequirement: "The delete button is enabled only after the confirmation text matches.",
      actions: {
        cancel: "Cancel",
        delete: "Delete chat",
        deleting: "Deleting...",
      },
      errors: {
        invalidRequest: "Direct chat delete request is invalid.",
        unauthorized: "Sign in again to delete this chat.",
        notFound: "This chat is no longer available.",
        conflict: "This chat has already been deleted from your list.",
        failed: "Failed to delete this direct chat. Try again.",
      },
    },
    secretEnvelope: {
      labels: {
        ratchetSessionId: "Ratchet session ID",
        ratchetCounter: "Ratchet counter",
        previousChainLength: "Previous chain length",
      },
      placeholders: {
        ratchetSessionId: "ratchet-session-1",
      },
      validation: {
        ciphertextRequired: "Secret message text is required.",
        ratchetSessionIdRequired: "Ratchet session ID is required for secret messages.",
        ratchetSessionIdInvalidPathSeparator: "Ratchet session ID contains invalid path separator.",
        ratchetCounterPositive: "Ratchet counter must be a positive number.",
        previousChainLengthNonNegative: "Previous chain length must be zero or greater.",
      },
      errors: {
        invalidEnvelope: "Secret envelope is invalid. Check ratchet fields and retry.",
        ratchetOutOfSync: "Secret ratchet state is out of sync. Update counters and retry.",
        sendFailed: "Failed to send secret message.",
      },
    },
    errors: {
      forbidden: "You don't have access to this chat.",
    },
  },
  profileAvatar: {
    eyebrow: "Profile",
    title: "Profile photo",
    youFallback: "You",
    previewAria: "{{displayName}} profile photo",
    hints: {
      chooseForPreview: "Choose an image to preview before replacing your avatar.",
      selected: "Selected: {{fileName}}",
    },
    actions: {
      reload: "Reload profile",
      closePanel: "Close profile panel",
      chooseImage: "Choose image",
      replacePhoto: "Replace photo",
      removePhoto: "Remove photo",
    },
    feedback: {
      updated: "Profile photo updated.",
      removed: "Profile photo removed.",
    },
    errors: {
      invalidRequest: "Unsupported image format or request payload.",
      forbidden: "You are not allowed to update this profile.",
      unauthorized: "Your session expired. Please sign in again.",
      reloadFailed: "Failed to refresh profile.",
      updateFailed: "Failed to update profile photo.",
      removeFailed: "Failed to remove profile photo.",
    },
  },
  profileSettings: {
    sectionEyebrow: "Profile",
    sectionTitle: "Settings",
    displayNameLabel: "Visible name",
    displayNameHint: "Shown in chats and contacts before nickname. Use 1-64 characters.",
    displayNameSave: "Save visible name",
    displayNameSaving: "Saving visible name...",
    displayNameSaved: "Visible name saved.",
    displayNameInvalidError: "Use 1-64 characters for visible name.",
    displayNameSaveError: "Unable to save visible name.",
    nicknameLabel: "Nickname",
    nicknameHint: "Use 3-32 letters, numbers, underscores, or dots. Leave empty to clear.",
    nicknameSave: "Save nickname",
    nicknameSaving: "Saving nickname...",
    nicknameSaved: "Nickname saved.",
    nicknameTakenError: "That nickname is already taken.",
    nicknameInvalidError: "Use 3-32 letters, numbers, underscores, or dots.",
    nicknameSaveError: "Unable to save nickname.",
    selectLabel: "Interface language",
    selectHint: "Language preference is saved to your profile.",
    optionRu: "Russian",
    optionEn: "English",
    themeLabel: "Theme",
    themeHint: "Theme preference is saved to your profile.",
    optionLight: "Light",
    optionDark: "Dark",
    saving: "Saving...",
    saveError: "Failed to save language preference.",
    themeSaveError: "Failed to save theme preference.",
  },
} as const;
